import { UtilService } from 'src/app/core/services/util/util.service';
import { Subscription } from 'rxjs';
import {
    Component,
    ViewEncapsulation,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    OnInit,
    OnDestroy,
    AfterViewInit,
    DOCUMENT,
    Type,
    computed,
    effect,
    inject,
    signal,
} from '@angular/core';
import { Router, NavigationStart, NavigationEnd, ActivatedRoute } from '@angular/router';
import { AppGlobalsModule } from './app-globals.module';
import { NotificationService } from './core/services/notification/notification.service';
import { Location } from '@angular/common';
import { environment } from 'src/environments/environment';
import { GoogleTextAdService } from './content/google-text-ad/google-text-ad.service';
import { BreakPointSizeService } from './core/services/break-point-size/break-point-size.service';
import { AngularPages, SiteLocations, UrlAddressService } from './core/services/url-address/url-address.service';
import { ScrollingService } from './scrolling.service';
import { HistoryRouteService } from './core/services/history-route/history-route.service';
import { I18nService } from './core/services/i18n/i18n.service';
import { MetaTagService } from './core/services/meta-tag/meta-tag.service';
import { ErrorsService } from './core/errors/errors-service/errors.service';
import { StorageService } from './core/services/storage/storage.service';
import { SiteModeService } from './core/services/site-mode/site-mode.service';
import { LessonsFilterService } from './content/lessons-filter-service';
import { isNihulSurfaceUrl } from './nihul/nihul-surfaces';
// import { ɵglobal, NgZone, inject, ApplicationRef } from '@angular/core';

// Android application function that would make play notidication
declare var mediaCheck: any;

//object from final.js in client page

@Component({
    selector: 'angular-app',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '(window:scroll)': 'checkScroll()',
    },
    standalone: false,
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
    // @HostListener('DOMContentLoaded')
    // DOMContentLoaded() {
    // // Logs the id of the element
    // // where the event is originally invoked.
    //     Info.Reload(true);
    // }
    globals = AppGlobalsModule;

    notification: string = '';
    public showNotification: boolean = false;
    public AngularPages = AngularPages;
    private firstLoadPage = true;
    private hasCompletedInitialNavigation = false;
    public pageReady = false;
    public siteLocations = SiteLocations;
    subscribes: Subscription[] = [];
    readonly activeAngularPage = signal<AngularPages | null>(null);
    readonly isMinimal = signal(false);
    readonly activeLocation = signal<SiteLocations | null>(null);
    readonly liveNotificationComponent = signal<Type<unknown> | null>(null);
    readonly userSummaryComponent = signal<Type<unknown> | null>(null);
    //the SHARED surfaces list (nihul-surfaces.ts), like the header's ad/broadcast-prompt suppression -
    //a live-broadcast popup (with sound) must not fire over an editor's screen
    readonly isNihulSurface = signal(false);
    readonly showLiveNotification = computed(
        () => !this.globals.isServer && !this.urlAddress.isDedicationDatePicker && !this.isMinimal() && !this.isNihulSurface(),
    );
    readonly showTopUserSummary = computed(() => this.activeLocation() === this.siteLocations.Users);
    readonly showBottomUserSummary = computed(() => {
        const activeAngularPage = this.activeAngularPage();
        return (
            activeAngularPage !== null &&
            activeAngularPage !== this.AngularPages.Filter &&
            !this.urlAddress.isDedicationDatePicker &&
            activeAngularPage !== this.AngularPages.Halacha &&
            this.activeLocation() !== this.siteLocations.Users &&
            !this.isMinimal()
        );
    });

    _errorsService = inject(ErrorsService);
    _historyRoute = inject(HistoryRouteService);
    _scrollingService = inject(ScrollingService);
    _i18n = inject(I18nService);
    _metaTagService = inject(MetaTagService);
    // Eagerly construct breakpoint state before child route templates render on client boot.
    private readonly _breakPointSizeService = inject(BreakPointSizeService);
    _cdr = inject(ChangeDetectorRef);
    _router = inject(Router);
    _route = inject(ActivatedRoute);
    urlAddress = inject(UrlAddressService);
    _notificationService = inject(NotificationService);
    _adService = inject(GoogleTextAdService);
    document = inject(DOCUMENT);
    _utils = inject(UtilService);
    _storageService = inject(StorageService);
    _siteMode = inject(SiteModeService);
    _location = inject(Location);
    _lessonsFilterService = inject(LessonsFilterService);
    private _mediaCheckTimer: ReturnType<typeof setTimeout> | null = null;
    private readonly _loadLiveNotificationEffect = effect(() => {
        if (this.showLiveNotification()) {
            void this._loadLiveNotification();
        }
    });
    private readonly _loadUserSummaryEffect = effect(() => {
        if (this.showTopUserSummary() || this.showBottomUserSummary()) {
            void this._loadUserSummary();
        }
    });

    ngOnInit() {
        if (AppGlobalsModule.isServer || !environment.production) {
            // In prod, the classes on the body come from Dotnet
            this._setBodyClasses();
            // console.log('In serve always "isAngularApp" return "true"');
        }

        //check and register to with and type (mobile or desktop)

        //seed before the first NavigationEnd - on a hard load of a nihul page the load effect
        //runs first and would otherwise start the live-player polling
        this.isNihulSurface.set(isNihulSurfaceUrl(this._location.path()));

        //subscribe to router navigations
        this.subscribes.push(
            this._router.events.subscribe((ev) => {
                if (!AppGlobalsModule.isServer && ev instanceof NavigationStart) {
                    this._historyRoute.isPopBackNavigation = false;
                    this._historyRoute.isPopForwardNavigation = false;
                    this._lessonsFilterService.resetFilter(true);
                    // We don't need to show error message after move to another page
                    this._errorsService.error = null;

                    if (ev.navigationTrigger !== 'popstate') {
                        this._historyRoute.navigationId = ev.id;
                    } else {
                        //going backwards or forwards with history buttons
                        if (ev.restoredState != null) {
                            if (ev.restoredState.navigationId < this._historyRoute.navigationId) {
                                //going backwards
                                this._historyRoute.isPopBackNavigation = true;
                                this._metaTagService.popFromHistoryTitles();
                            } else {
                                this._historyRoute.isPopForwardNavigation = true;
                            }

                            //update navigtion id
                            this._historyRoute.navigationId = ev.restoredState.navigationId;
                        }
                    }
                    if (this.firstLoadPage) {
                        this.urlAddress.setFirstLoadPage(false);
                        //from now this is not the first page in the app
                        this.firstLoadPage = false;
                    }
                }
                if (ev instanceof NavigationEnd) {
                    this.isNihulSurface.set(isNihulSurfaceUrl(ev.urlAfterRedirects));
                    this._metaTagService.setMetaTagByProperty('og:url', this._i18n.getProdDomain() + this._location.path());
                    if (!AppGlobalsModule.isServer) {
                        // We don't want to scroll on the first time because it conflict with angular universal
                        this._scrollingService.lastScrolledPage = ev.urlAfterRedirects;
                        //if fragment defined  and also it is the first page load we need to prevent the browser from going to the last scroll position
                        if (this._route.snapshot.fragment && this.firstLoadPage) {
                            console.log('Preventing scroll restoration on first load with fragment');
                            history.scrollRestoration = 'manual';
                        }
                        if (!this._route.snapshot.fragment && !this.firstLoadPage) {
                            // this.store.HandleScroll(this._router);
                        } else {
                            this._scrollingService.readyToScroll = true;
                        }

                        //notify Google Analytics in inner pages navigation
                        if (this.hasCompletedInitialNavigation) {
                            // Not needed since GA4 'Enhanced measurement'
                            // this._analytics.virtualPage('pageview');

                            // Refresh Ads
                            this._adService.refreshAllAds();
                        } else {
                            this.hasCompletedInitialNavigation = true;
                        }
                        // if (this.readyToLoad&&this.pageReady)
                        // {
                        //     Info.Reload(true);
                        // }

                        //fix layout buttons affter navigation
                        this.fixLayoutLinks();

                        // In Android application we need to open custome notification scheme
                        if (typeof mediaCheck !== 'undefined' && mediaCheck != null) {
                            if (this._mediaCheckTimer) {
                                clearTimeout(this._mediaCheckTimer);
                            }
                            this._mediaCheckTimer = AppGlobalsModule.setTimeoutIsNotServer(() => {
                                mediaCheck();
                                this._mediaCheckTimer = null;
                            }, 500);
                        }
                    }
                }

                if (!AppGlobalsModule.isServer && ev instanceof NavigationStart) {
                    this._scrollingService.lastScrolledPage = '';
                    this._scrollingService.readyToScroll = false;
                    this.pageReady = false;
                }
            }),
        );

        this.subscribes.push(
            this._notificationService.notification$.subscribe((message) => {
                this.notification = message ?? '';
                this.showNotification = !!message;
                this._cdr.markForCheck();
            }),
        );
        this.subscribes.push(
            this._siteMode.getMinimalMode$().subscribe((mode) => {
                this.isMinimal.set(mode);
                this._cdr.markForCheck();
            }),
        );
        this.subscribes.push(
            this.urlAddress.getActiveAngularPage().subscribe((activeAngularPage) => {
                this.activeAngularPage.set(activeAngularPage);
                this._cdr.markForCheck();
            }),
        );
        this.subscribes.push(
            this.urlAddress.getActiveLocation$().subscribe((active) => {
                this.activeLocation.set(active);
                this._cdr.markForCheck();
            }),
        );
    }

    public isAfterViewInit = false;

    ngAfterViewInit(): void {
        this.isAfterViewInit = true;
        this._cdr.markForCheck();
    }

    private _setBodyClasses(): void {
        const langClass = this._i18n.getLangClass();
        const lang = this._i18n.getLangshort();
        this.document.body.classList.add(langClass);
        this.document.documentElement.lang = lang;
        this.document.documentElement.className = langClass;
        this.document.documentElement.dir = lang === 'he' ? 'rtl' : 'ltr';
    }

    checkScroll() {
        if (this.pageReady) {
            this._scrollingService.setScrollLocation(this._router);
        }
    }

    fixLayoutLinks() {
        //can't access DOM in universal
        if (!AppGlobalsModule.isServer) {
            //change share buttons
            if (this._breakPointSizeService.isMobileSize()) {
                let shareLinks = this.document.getElementsByClassName('Share')[0];

                if (shareLinks) {
                    shareLinks.remove();
                }
            }
        }
    }

    ngOnDestroy(): void {
        if (this._mediaCheckTimer) {
            clearTimeout(this._mediaCheckTimer);
            this._mediaCheckTimer = null;
        }
        this._utils.unsubscribe(this.subscribes);
    }

    private async _loadLiveNotification(): Promise<void> {
        if (this.liveNotificationComponent()) {
            return;
        }

        const { LiveNotificationComponent } = await import('./video-player/live-notification/live-notification.component');
        this.liveNotificationComponent.set(LiveNotificationComponent);
    }

    private async _loadUserSummary(): Promise<void> {
        if (this.userSummaryComponent()) {
            return;
        }

        const { UserSummaryComponent } = await import('./user/user-summary/user-summary.component');
        this.userSummaryComponent.set(UserSummaryComponent);
    }
}
